// apps/client/src/hooks/useContactActivity.ts
//
// Company-wide Contacts Activity feed. Converged onto the canonical loan-keyed
// store. Phase 4 (Contract, 2026-06): the 4 legacy per-page collections were
// backfilled into the canonical store and dropped from this read path. The feed
// now reads `loan_contacts` (canonical) + `client_contacts` (Client Care, which
// stays - it is client-keyed and holds client-only contacts that can never be
// loan-keyed), then aggregates them through the shared engine: global dedupe by
// entry id (canonical wins), references unioned (a mirrored Client Care contact
// keeps both its loanId and clientId), tombstoned entries suppressed. Richer than
// the old boolean feed: channels, outcome, contact date, server-stamped actor,
// and the loan/client deep-link targets.

import { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot, DocumentData } from 'firebase/firestore';
import { db } from '../lib/firebase';
import {
  CANONICAL_COLLECTION,
  type CanonicalContactDoc,
  type LegacyContactDoc,
  type FeedContribution,
  aggregateContactFeed,
  flattenLegacyDoc,
  normalizeCanonicalEntries,
  extractTombstoneIds,
} from '../utils/contacts/loanContactMerge';

export type {
  ContactActivityEntry,
  FeedSourcePage,
} from '../utils/contacts/loanContactMerge';

const CLIENT_COLLECTION = 'client_contacts';
// canonical + client_contacts (legacy per-page collections dropped in Phase 4)
const TOTAL_SOURCES = 2;

export function useContactActivity(companyId: string | undefined) {
  const [contribsBySource, setContribsBySource] = useState<Record<string, FeedContribution[]>>({});
  const [tombstonedIds, setTombstonedIds] = useState<Set<string>>(new Set());
  const [loadedSources, setLoadedSources] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  // No company selected → nothing to load (don't hang on a perpetual spinner).
  const loading = !!companyId && loadedSources.size < TOTAL_SOURCES;

  useEffect(() => {
    if (!companyId) return;

    setContribsBySource({});
    setTombstonedIds(new Set());
    setLoadedSources(new Set());
    setError(null);

    const unsubscribes: (() => void)[] = [];
    const markLoaded = (name: string) =>
      setLoadedSources((prev) => new Set(prev).add(name));

    // --- Canonical loan_contacts ---
    unsubscribes.push(
      onSnapshot(
        query(collection(db, `companies/${companyId}/${CANONICAL_COLLECTION}`)),
        (snap) => {
          const contribs: FeedContribution[] = [];
          const tombs = new Set<string>();
          snap.forEach((docSnap) => {
            const data = docSnap.data() as DocumentData as CanonicalContactDoc;
            const loanId = data.loanId || docSnap.id;
            if (!loanId) return;
            for (const entry of normalizeCanonicalEntries(data)) {
              contribs.push({
                entry,
                canonical: true,
                feedSourcePage: entry.sourcePage,
                customerName: data.customerName || '',
                loanId,
              });
            }
            for (const id of extractTombstoneIds(data)) tombs.add(id);
          });
          setContribsBySource((prev) => ({ ...prev, [CANONICAL_COLLECTION]: contribs }));
          setTombstonedIds(tombs);
          markLoaded(CANONICAL_COLLECTION);
        },
        (err) => {
          console.error(`[useContactActivity] Error on ${CANONICAL_COLLECTION}:`, err);
          setError('Failed to load contact history');
          markLoaded(CANONICAL_COLLECTION);
        }
      )
    );

    // --- Client Care (client-keyed; legacy boolean entry shape) ---
    unsubscribes.push(
      onSnapshot(
        query(collection(db, `companies/${companyId}/${CLIENT_COLLECTION}`)),
        (snap) => {
          const contribs: FeedContribution[] = [];
          snap.forEach((docSnap) => {
            const data = docSnap.data() as DocumentData as LegacyContactDoc & { clientId?: string };
            // entries are the legacy boolean shape — map via a dummy source then
            // tag the contribution Client Care (the aggregator uses feedSourcePage).
            for (const entry of flattenLegacyDoc(data, 'Discharges')) {
              contribs.push({
                entry,
                canonical: false,
                feedSourcePage: 'Client Care',
                customerName: data.customerName || '',
                clientId: data.clientId || data.settlementKey || docSnap.id,
              });
            }
          });
          setContribsBySource((prev) => ({ ...prev, [CLIENT_COLLECTION]: contribs }));
          markLoaded(CLIENT_COLLECTION);
        },
        (err) => {
          console.error(`[useContactActivity] Error on ${CLIENT_COLLECTION}:`, err);
          setError('Failed to load Client Care contacts');
          markLoaded(CLIENT_COLLECTION);
        }
      )
    );

    return () => unsubscribes.forEach((unsub) => unsub());
  }, [companyId]);

  const entries = useMemo(() => {
    const all: FeedContribution[] = [];
    for (const list of Object.values(contribsBySource)) all.push(...list);
    return aggregateContactFeed(all, tombstonedIds);
  }, [contribsBySource, tombstonedIds]);

  return { entries, loading, error };
}
